/**
 * Holding page behaviour.
 *
 * 1. Click triggers - any [data-open-enquiry] opens the shared enquiry popup
 *    and writes its data-trigger-location into the form's hidden field.
 * 2. Popup mechanics - open / close (button, ESC, backdrop), focus trap,
 *    body scroll lock, focus return, focus first form field on open.
 * 3. trigger_location population - the only hidden field this script touches.
 *    All other hidden fields (source, utm_*, gclid, referrer) are populated
 *    server-side via gform_field_value_* filters in inc/gravity-forms.php
 *    and the form JSON's defaultValue, so they survive a JS-off page load.
 * 4. GA4 events - popup_open / form_start / form_submit / form_error /
 *    popup_close. Every event carries source = mynewhq-holding-page.
 * 5. Scroll-to-top - [data-mnhq-scroll-top] elements (logo in minimal
 *    header / footer) smooth-scroll to the top of the page.
 *
 * Enqueued only on the page-holding.php template (see inc/styles-scripts.php).
 * Depends on jQuery for the Gravity Forms AJAX lifecycle hooks.
 */
(function () {
	'use strict';

	var SOURCE   = 'mynewhq-holding-page';
	var POPUP_ID = 'mnhq-holding-enquiry-popup';

	var popupLastFocus       = null;
	var submitTracked        = {};
	var startTrackedInstance = {};

	function ready(fn) {
		if (document.readyState !== 'loading') {
			fn();
		} else {
			document.addEventListener('DOMContentLoaded', fn);
		}
	}

	function prefersReducedMotion() {
		return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	}

	function track(event, payload) {
		var data = {
			event: event,
			source: SOURCE,
			page_path: window.location.pathname || ''
		};
		if (payload) {
			Object.keys(payload).forEach(function (k) { data[k] = payload[k]; });
		}
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push(data);
		if (typeof window.gtag === 'function') {
			window.gtag('event', event, data);
		}
		if (window.console && window.console.debug) {
			window.console.debug('[mnhq-holding]', event, data);
		}
	}

	// -----------------------------------------------------------------
	// trigger_location population
	// -----------------------------------------------------------------
	function writeTriggerLocation(wrapper, value) {
		if (!wrapper) return;
		wrapper.setAttribute('data-trigger-location', value);
		var input = wrapper.querySelector('[data-mnhq-name="trigger_location"]');
		if (input) { input.value = value; }
	}

	function processForms() {
		document.querySelectorAll('.mnhq-holding-form').forEach(function (wrapper) {
			writeTriggerLocation(wrapper, wrapper.getAttribute('data-trigger-location') || '');
			wireFormInstance(wrapper);
		});
	}

	// -----------------------------------------------------------------
	// Form lifecycle GA4
	// -----------------------------------------------------------------
	function wireFormInstance(wrapper) {
		if (wrapper.dataset.mnhqHoldingBound) return;
		wrapper.dataset.mnhqHoldingBound = '1';

		var form = wrapper.querySelector('form');
		if (!form) return;

		var formId      = wrapper.getAttribute('data-form-id') || '';
		var instanceKey = 'holding:' + formId;

		form.addEventListener('focusin', function () {
			if (startTrackedInstance[instanceKey]) return;
			startTrackedInstance[instanceKey] = true;
			track('form_start', {
				trigger_location: wrapper.getAttribute('data-trigger-location') || '',
				form_id: formId
			});
		});
	}

	function reportFieldErrors(wrapper) {
		var triggerLoc = wrapper.getAttribute('data-trigger-location') || '';
		var errors     = wrapper.querySelectorAll('.gfield_error');
		errors.forEach(function (field) {
			var label = field.querySelector('.gfield_label');
			track('form_error', {
				trigger_location: triggerLoc,
				field_name: label ? label.textContent.trim() : 'unknown'
			});
		});
	}

	function bindGravityHooks() {
		if (!window.jQuery) return;
		var $ = window.jQuery;

		$(document).on('gform_post_render', function () {
			document.querySelectorAll('.mnhq-holding-form').forEach(function (wrapper) {
				writeTriggerLocation(wrapper, wrapper.getAttribute('data-trigger-location') || '');
				wireFormInstance(wrapper);
				reportFieldErrors(wrapper);
			});
		});

		$(document).on('gform_confirmation_loaded', function (event, formId) {
			var wrapper = document.querySelector(
				'.mnhq-holding-form[data-form-id="' + formId + '"]'
			);
			if (!wrapper) return;

			if (!submitTracked[formId]) {
				submitTracked[formId] = true;
				track('form_submit', {
					trigger_location: wrapper.getAttribute('data-trigger-location') || '',
					form_id: formId
				});
			}

			var heading = wrapper.querySelector('.mnhq-contact-form__success-heading');
			if (heading && typeof heading.focus === 'function') {
				heading.focus();
			}
		});
	}

	// -----------------------------------------------------------------
	// Popup open / close
	// -----------------------------------------------------------------
	function openPopup(popup, triggerEl) {
		if (popup.classList.contains('is-open')) return;
		popupLastFocus = triggerEl || document.activeElement;

		var triggerLoc = triggerEl ? (triggerEl.getAttribute('data-trigger-location') || '') : '';

		document.querySelectorAll('.mnhq-holding-form').forEach(function (wrapper) {
			writeTriggerLocation(wrapper, triggerLoc);
		});

		popup.removeAttribute('hidden');
		popup.setAttribute('aria-hidden', 'false');
		popup.classList.add('is-open');
		document.body.classList.add('mnhq-popup-open');

		track('popup_open', { trigger_location: triggerLoc });

		// Focus the first visible form field. Defer one tick so the browser has
		// applied layout (the dialog moves from display:none to display:flex).
		// Skip Gravity Forms' honeypot field (visually hidden via CSS) — the
		// browser refuses to focus invisible elements anyway, but iterating
		// lets us land on the next visible input instead of giving up.
		setTimeout(function () {
			var candidates = popup.querySelectorAll(
				'input:not([type="hidden"]):not([disabled]):not([type="submit"]):not([type="button"]), textarea:not([disabled]), select:not([disabled])'
			);
			var focused = false;
			for (var i = 0; i < candidates.length; i++) {
				var el = candidates[i];
				// offsetParent is null when the element (or any ancestor) is
				// display:none, which is how GF hides the honeypot wrapper.
				if (el.offsetParent === null) continue;
				if (typeof el.focus === 'function') {
					el.focus();
					focused = (document.activeElement === el);
					if (focused) break;
				}
			}
			if (!focused) {
				var heading = popup.querySelector('.mnhq-building-popup__heading');
				if (heading && typeof heading.focus === 'function') {
					if (!heading.hasAttribute('tabindex')) {
						heading.setAttribute('tabindex', '-1');
					}
					heading.focus({ preventScroll: true });
				}
			}
		}, 30);
	}

	function closePopup(popup, method) {
		if (!popup.classList.contains('is-open')) return;

		var wrapper    = popup.querySelector('.mnhq-holding-form');
		var triggerLoc = wrapper ? (wrapper.getAttribute('data-trigger-location') || '') : '';

		popup.classList.remove('is-open');
		popup.setAttribute('aria-hidden', 'true');
		popup.setAttribute('hidden', '');
		document.body.classList.remove('mnhq-popup-open');

		track('popup_close', { trigger_location: triggerLoc, method: method || 'unknown' });

		if (popupLastFocus && typeof popupLastFocus.focus === 'function') {
			popupLastFocus.focus();
		}
		popupLastFocus = null;
	}

	function initPopup() {
		var popup = document.getElementById(POPUP_ID);
		if (!popup) return;

		document.addEventListener('click', function (e) {
			var trigger = e.target.closest('[data-open-enquiry]');
			if (!trigger) return;
			e.preventDefault();
			openPopup(popup, trigger);
		});

		popup.querySelectorAll('[data-mnhq-popup-close]').forEach(function (el) {
			el.addEventListener('click', function () {
				var method = el.classList.contains('mnhq-building-popup__backdrop')
					? 'backdrop'
					: 'button';
				closePopup(popup, method);
			});
		});

		document.addEventListener('keydown', function (e) {
			if (e.key === 'Escape' && popup.classList.contains('is-open')) {
				closePopup(popup, 'esc');
			}
		});

		popup.addEventListener('keydown', function (e) {
			if (e.key !== 'Tab' || !popup.classList.contains('is-open')) return;
			var focusables = popup.querySelectorAll(
				'button:not([disabled]), [href], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
			);
			if (!focusables.length) return;
			var first = focusables[0];
			var last  = focusables[focusables.length - 1];
			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		});
	}

	// -----------------------------------------------------------------
	// Scroll-to-top (logo in minimal header + footer)
	// -----------------------------------------------------------------
	function initScrollTop() {
		document.querySelectorAll('[data-mnhq-scroll-top]').forEach(function (el) {
			el.addEventListener('click', function (e) {
				e.preventDefault();
				var smooth = !prefersReducedMotion();
				window.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' });
			});
		});
	}

	// -----------------------------------------------------------------
	// In-page anchor links (header nav + drawer nav). We let the
	// browser handle the URL hash, but drive the scroll explicitly so
	// the behaviour is consistent across browsers and respects the
	// scroll-margin-top we set on each section.
	// -----------------------------------------------------------------
	function initAnchorLinks() {
		var selector = '.mnhq-header__nav-link[href^="#"], .mnhq-header__drawer-link[href^="#"]';
		document.querySelectorAll(selector).forEach(function (link) {
			link.addEventListener('click', function (e) {
				var href = link.getAttribute('href') || '';
				if (href.length < 2 || href.charAt(0) !== '#') return;
				var target = document.getElementById(href.slice(1));
				if (!target) return;
				e.preventDefault();
				var smooth = !prefersReducedMotion();
				target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
				if (window.history && typeof window.history.replaceState === 'function') {
					window.history.replaceState(null, '', href);
				}
			});
		});
	}

	// -----------------------------------------------------------------
	// Mobile drawer (hamburger → in-header panel with section links).
	// -----------------------------------------------------------------
	function initDrawer() {
		var drawer = document.getElementById('mnhq-holding-drawer');
		var toggle = document.querySelector('[data-mnhq-drawer-toggle]');
		var header = document.querySelector('.mnhq-header--holding');
		if (!drawer || !toggle) return;

		var openLabel  = toggle.getAttribute('aria-label') || 'Open menu';
		var closeLabel = 'Close menu';

		// The drawer fills the viewport from the bottom of the header down,
		// so it needs to know how tall the header actually is. Re-measure
		// on resize for landscape rotation etc.
		function syncHeaderHeight() {
			if (!header) return;
			document.documentElement.style.setProperty(
				'--mnhq-holding-header-h',
				header.offsetHeight + 'px'
			);
		}
		syncHeaderHeight();
		window.addEventListener('resize', syncHeaderHeight);

		function openDrawer() {
			// Land at the top of the page before the menu opens. Instant rather
			// than smooth: opening the drawer locks body scroll, which would cut
			// a smooth scroll short, and the opening panel hides the jump anyway.
			window.scrollTo({ top: 0, behavior: 'auto' });
			drawer.removeAttribute('hidden');
			drawer.setAttribute('aria-hidden', 'false');
			drawer.classList.add('is-open');
			toggle.setAttribute('aria-expanded', 'true');
			toggle.setAttribute('aria-label', closeLabel);
			document.body.classList.add('mnhq-holding-drawer-open');
		}

		function closeDrawer() {
			if (!drawer.classList.contains('is-open')) return;
			drawer.classList.remove('is-open');
			drawer.setAttribute('aria-hidden', 'true');
			drawer.setAttribute('hidden', '');
			toggle.setAttribute('aria-expanded', 'false');
			toggle.setAttribute('aria-label', openLabel);
			document.body.classList.remove('mnhq-holding-drawer-open');
		}

		toggle.addEventListener('click', function () {
			if (drawer.classList.contains('is-open')) {
				closeDrawer();
			} else {
				openDrawer();
			}
		});

		// Anchor link inside the drawer closes the drawer; the browser then
		// handles the smooth-scroll to the section.
		drawer.querySelectorAll('[data-mnhq-drawer-link]').forEach(function (link) {
			link.addEventListener('click', closeDrawer);
		});

		// The Enquire button inside the drawer is also a popup trigger via
		// the global delegated click handler; close the drawer first so the
		// popup opens in front of a clean header.
		var drawerCta = drawer.querySelector('[data-open-enquiry]');
		if (drawerCta) {
			drawerCta.addEventListener('click', closeDrawer);
		}

		// ESC closes the drawer (only when the enquiry popup isn't already
		// open — the popup's own ESC handler would have taken precedence).
		document.addEventListener('keydown', function (e) {
			if (e.key !== 'Escape') return;
			if (!drawer.classList.contains('is-open')) return;
			closeDrawer();
			toggle.focus();
		});

		// Auto-close if the viewport grows past the mobile breakpoint while
		// the drawer is open (e.g. landscape rotation).
		window.addEventListener('resize', function () {
			if (window.innerWidth >= 768) {
				closeDrawer();
			}
		});
	}

	// -----------------------------------------------------------------
	// Boot
	// -----------------------------------------------------------------
	ready(function () {
		initPopup();
		initScrollTop();
		initAnchorLinks();
		initDrawer();
		if (window.jQuery) {
			bindGravityHooks();
			processForms();
		} else {
			processForms();
		}
	});
})();
